import { useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';

import { SalesRatioReport } from '@/domain/models/SalesReport';
import { getClient } from '@/lib/restClient';
import { FilterParams, SalesReportService } from '@/services/SalesReport';
import { SalesFilter } from '@/services/SalesReport/hooks/useSalesReportFilter';
import { useSalesRateState } from '@/store/sales_aggregation';

interface Report {
  salesRatio: SalesRatioReport;
  isLoading: boolean;
  error: Error | null;
}

const useSalesRatio = (params: FilterParams, enabled = false): Report => {
  const salesReportService = new SalesReportService(getClient());

  const {
    data: salesRatio = {} as SalesRatioReport,
    error,
    isLoading,
    isFetching,
    isPreviousData,
  } = useQuery<SalesRatioReport, Error>(
    ['salesRatio', params],
    () => {
      return salesReportService.aggregateSalesRatios(params);
    },
    {
      enabled,
      keepPreviousData: true,
    }
  );
  return {
    salesRatio,
    isLoading: isLoading || (isFetching && isPreviousData),
    error: error,
  };
};

interface FilteringSalesRatio {
  salesRatio: SalesRatioReport;
  isLoading: boolean;
  error: Error | null;
  filter: SalesFilter;
  setFilter: (filter: SalesFilter) => void;
}

export const useSalesRatioFilter = (): FilteringSalesRatio => {
  const {
    from,
    to,
    masterBrandIDs,
    masterBrandNames,
    masterStoreIDs,
    deliveryServiceIds,
    deliveryTypes,
    authStoreIds,
    shouldFetch,
    setFrom,
    setTo,
    setMasterBrandIDs,
    setMasterBrandNames,
    setMasterStoreIDs,
    setDeliveryServiceIds,
    setDeliveryTypes,
    setAuthStoreIds,
    setShouldFetch,
  } = useSalesRateState();

  const { salesRatio, isLoading, error } = useSalesRatio(
    {
      startDate: from,
      endDate: to,
      masterBrandIDs,
      masterBrandNames,
      masterStoreIDs,
      deliveryServiceIds,
      deliveryTypes,
      authStoreIds,
    },
    shouldFetch
  );

  useEffect(() => {
    if (shouldFetch && !isLoading) {
      setShouldFetch(false);
    }
  }, [shouldFetch, isLoading, setShouldFetch]);

  const setFilter = useCallback(
    (filter: SalesFilter, submit = false) => {
      setFrom(filter.from);
      setTo(filter.to);
      setMasterBrandIDs(filter.masterBrandIDs);
      setMasterBrandNames(filter.masterBrandNames);
      setMasterStoreIDs(filter.masterStoreIDs);
      setDeliveryServiceIds(filter.deliveryServiceIds);
      setDeliveryTypes(filter.deliveryTypes);
      setAuthStoreIds(filter.authStoreIds);

      if (submit) {
        setShouldFetch(true);
      }
    },
    [
      setFrom,
      setTo,
      setMasterBrandIDs,
      setMasterBrandNames,
      setMasterStoreIDs,
      setDeliveryServiceIds,
      setDeliveryTypes,
      setAuthStoreIds,
      setShouldFetch,
    ]
  );

  const filter: SalesFilter = {
    from,
    to,
    masterBrandIDs,
    masterBrandNames,
    masterStoreIDs,
    deliveryServiceIds,
    deliveryTypes,
    authStoreIds,
  };

  return {
    salesRatio,
    isLoading,
    error,
    filter,
    setFilter,
  };
};
